import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getCatalogueByCode } from "../../api/common/catalogues.ts";
import { getStockMoveList } from "../../api/stockmoves/index.ts";
import { getWarehouseList } from "../../api/warehouses/index.ts";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import syntheticStockMoveService from "../../services/syntheticStockMoveService";
import SyntheticOutboundStockMoveForm from "./SyntheticOutboundStockMoveForm";
import SyntheticOutboundCsvUploadModal from "./SyntheticOutboundCsvUploadModal";
import { usePersistedPagination } from "../../hooks/usePersistedPagination";

const PAGE_SIZE = 500;

const pickFirstCatalogueEntry = (res) => {
  const d = res?.data;
  return Array.isArray(d) ? d[0] : d;
};

const SyntheticOutboundStockMoveCrud = () => {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const [moveTypeId, setMoveTypeId] = useState(null);
  const [startDate, setStartDate] = useState(() => dayjs().subtract(1, "week"));
  const [endDate, setEndDate] = useState(() => dayjs());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehouseMap, setWarehouseMap] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  const [paginationModel, setPaginationModel] = usePersistedPagination({
    defaultPageSize: 25,
  });

  useEffect(() => {
    const loadMoveType = async () => {
      try {
        // Keep compatibility with both possible catalogue codes.
        const resPrimary = await getCatalogueByCode("MOVETYPE_SYNTHETIC_OUTBOUND");
        let moveType = pickFirstCatalogueEntry(resPrimary);

        setMoveTypeId(moveType?.id ?? null);
      } catch (error) {
        setMoveTypeId(null);
      }
    };
    loadMoveType();
  }, []);

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const whRes = await getWarehouseList();
        const whList = Array.isArray(whRes.data) ? whRes.data : [];
        const map = whList.reduce((acc, wh) => {
          acc[wh.id] = wh.name || String(wh.id);
          return acc;
        }, {});
        setWarehouseMap(map);
      } catch (error) {
        setWarehouseMap({});
      }
    };
    loadWarehouses();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedCompany?.id || !moveTypeId) {
        setRows([]);
        return;
      }
      setLoading(true);
      try {
        const all = [];
        let pageNumber = 1;
        while (true) {
          const batch = await getStockMoveList({
            companyId: selectedCompany.id,
            moveTypeId,
            pageSize: PAGE_SIZE,
            pageNumber,
            startDate: startDate?.format("YYYY-MM-DD"),
            endDate: endDate?.format("YYYY-MM-DD"),
          });
          const list = Array.isArray(batch) ? batch : [];
          all.push(...list);
          if (list.length < PAGE_SIZE) break;
          pageNumber += 1;
        }
        setRows(all);
      } catch (error) {
        openSnack(handleError(error), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCompany?.id, moveTypeId, startDate, endDate, openSnack]);

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("¿Seguro que quieres eliminar esta salida?")) return;
      try {
        await syntheticStockMoveService.deleteSyntheticOutbound(id);
        setRows((prev) => prev.filter((row) => row.id !== id));
        openSnack("Salida eliminada exitosamente", "success");
      } catch (error) {
        openSnack(handleError(error), "error");
      }
    },
    [openSnack]
  );

  const handleEdit = useCallback((id) => {
    setEditingId(id);
  }, []);

  const openCreateModal = useCallback(() => {
    setCreating(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingId(null);
  }, []);

  const closeCreateModal = useCallback(() => {
    setCreating(false);
  }, []);

  const openCsvUploadModal = useCallback(() => {
    setCsvUploadOpen(true);
  }, []);

  const closeCsvUploadModal = useCallback(() => {
    setCsvUploadOpen(false);
  }, []);

  const refreshRows = useCallback(async () => {
    if (!selectedCompany?.id || !moveTypeId) return;
    setLoading(true);
    try {
      const all = [];
      let pageNumber = 1;
      while (true) {
        const batch = await getStockMoveList({
          companyId: selectedCompany.id,
          moveTypeId,
          pageSize: PAGE_SIZE,
          pageNumber,
          startDate: startDate?.format("YYYY-MM-DD"),
          endDate: endDate?.format("YYYY-MM-DD"),
        });
        const list = Array.isArray(batch) ? batch : [];
        all.push(...list);
        if (list.length < PAGE_SIZE) break;
        pageNumber += 1;
      }
      setRows(all);
    } catch (error) {
      openSnack(handleError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.id, moveTypeId, startDate, endDate, openSnack]);

  const handleCsvUploadSuccess = useCallback(async () => {
    await refreshRows();
  }, [refreshRows]);

  const handleEditSuccess = useCallback(async () => {
    setEditingId(null);
    await refreshRows();
  }, [refreshRows]);

  const handleCreateSuccess = useCallback(async () => {
    setCreating(false);
    await refreshRows();
  }, [refreshRows]);

  const columns = useMemo(
    () => [
      {
        field: "moveDate",
        headerName: "Fecha movimiento",
        flex: 0.18,
        minWidth: 170,
        valueFormatter: (value) =>
          value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "",
      },
      {
        field: "productCode",
        headerName: "Producto",
        flex: 0.14,
        minWidth: 120,
        valueGetter: (value, row) => row.product?.code || "",
      },
      {
        field: "providerName",
        headerName: "Proveedor",
        flex: 0.18,
        minWidth: 160,
        valueGetter: (value, row) => {
          if (!row.providerCode && !row.providerName) return "-";
          return `${row.providerCode || ""} - ${row.providerName || ""}`.trim();
        },
      },
      {
        field: "workOrderId",
        headerName: "Orden de trabajo",
        flex: 0.12,
        minWidth: 120,
        valueGetter: (value, row) => row.workOrderId || "-",
      },
      {
        field: "purchaseOrderCode",
        headerName: "Código OC",
        flex: 0.1,
        minWidth: 100,
        valueGetter: (value, row) => row.purchaseOrderCode || "-",
      },
      {
        field: "quantity",
        headerName: "Cantidad",
        type: "number",
        flex: 0.12,
        minWidth: 100,
      },
      {
        field: "unitCost",
        headerName: "Costo unitario",
        type: "number",
        flex: 0.12,
        minWidth: 110,
      },
      {
        field: "value",
        headerName: "Valor",
        type: "number",
        flex: 0.12,
        minWidth: 110,
        valueGetter: (value, row) =>
          (Number(row.quantity) || 0) * (Number(row.unitCost) || 0),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Acciones",
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key={`edit-${params.id}`}
            icon={<EditIcon />}
            label="Editar"
            className="textPrimary"
            onClick={() => handleEdit(params.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={`delete-${params.id}`}
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => handleDelete(params.id)}
            sx={{ color: "error.main" }}
          />,
        ],
      },
    ],
    [handleDelete, handleEdit]
  );

  if (!selectedCompany) {
    return (
      <Box p={4}>
        <Typography>Seleccione una compañía primero</Typography>
      </Box>
    );
  }

  if (!moveTypeId) {
    return (
      <Box p={4}>
        <Typography>Cargando configuración de movimientos…</Typography>
      </Box>
    );
  }

  return (
    <Box width="100%" sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <MyDatePicker
              label="Fecha Inicial"
              value={startDate}
              onChange={(newValue) => setStartDate(dayjs(newValue))}
            />
            <MyDatePicker
              label="Fecha Final"
              value={endDate}
              onChange={(newValue) => setEndDate(dayjs(newValue))}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={openCsvUploadModal}
            >
              Cargar CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateModal}
            >
              Nueva salida
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={creating} onClose={closeCreateModal} fullWidth maxWidth="sm">
        <DialogTitle>Nueva salida sintética</DialogTitle>
        <DialogContent>
          {creating && (
            <SyntheticOutboundStockMoveForm
              companyId={selectedCompany?.id}
              onSubmit={handleCreateSuccess}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editingId != null} onClose={closeEditModal} fullWidth maxWidth="sm">
        <DialogTitle>Editar salida sintética</DialogTitle>
        <DialogContent>
          {editingId != null && (
            <SyntheticOutboundStockMoveForm
              id={editingId}
              companyId={selectedCompany?.id}
              onSubmit={handleEditSuccess}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={csvUploadOpen} onClose={closeCsvUploadModal} fullWidth maxWidth="sm">
        <DialogTitle>Cargar salidas desde CSV</DialogTitle>
        <DialogContent>
          {csvUploadOpen && (
            <SyntheticOutboundCsvUploadModal
              companyId={selectedCompany?.id}
              onSuccess={handleCsvUploadSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SyntheticOutboundStockMoveCrud;
